import { Checkbox, Paper, Select, Stack } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import DataTables from 'components/DataTables';
import React, { useEffect, useState } from 'react';
import { courseService } from 'services/lcms/course.service';
import { assessmentScheduleService } from 'services/lia/assessmentScheduleService';
import MaterialLink from './MaterialLink';

interface ExistingActivityProps {
  onChange: (_data: any) => void;
}

const PARAMS = {
  pageNumber: 1,
  pageSize: 10000
};

interface IActivityData {
  activityName: string;
  activityType: string;
  activityId: string;
  activityUrl: string;
  selected: boolean;
  courseId: string;
}

interface IActivityDataSelected extends IActivityData {
  selected: boolean;
  courseId: string;
}

const ExistingActivity: React.FC<ExistingActivityProps> = ({ onChange }) => {
  const [selectedCourses, setSelectedCourses] = useState<string | null>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [debounced] = useDebouncedValue(searchValue, 1000);
  const [activityData, setActivityData] = useState<IActivityDataSelected[]>([]);

  const { data, isFetching } = useQuery({
    queryKey: ['searchCourse', PARAMS, debounced],
    queryFn: () => assessmentScheduleService.searchCourse({ ...PARAMS, search: debounced }),
    enabled: Boolean(debounced)
  });

  const { data: dataSection, isFetching: loadingGetSection } = useQuery({
    queryKey: ['getSectionByCourse', selectedCourses],
    queryFn: () => courseService.getSectionByCourse({ ...PARAMS, courseid: selectedCourses }),
    enabled: Boolean(selectedCourses)
  });

  useEffect(() => {
    if (dataSection?.data.length > 0) {
      const activities = dataSection?.data.flatMap((item: { modules: any }) => item.modules);
      setActivityData(
        activities
          ? activities.map(
              (activity: { name: string; id: number; modName: string; url: string }) => ({
                activityName: activity.name,
                activityType: activity.modName,
                activityId: activity.id.toString(),
                activityUrl: activity.url,
                courseName: data?.data.filter(
                  (course: { courseID: string; courseName: string }) =>
                    course.courseID === selectedCourses
                )[0].courseName,
                courseId: selectedCourses || '',
                selected: false
              })
            )
          : []
      );
    }
    return () => {
      setActivityData([]);
    };
  }, [dataSection?.data]);

  // useEffect(() => {
  //   if (debounced) {
  //     setActivityData((prevData) =>
  //       prevData.filter(
  //         (item) => item.activityName.toLocaleLowerCase() === debounced.toLocaleLowerCase()
  //       )
  //     );
  //   }
  //   return () => {
  //     setActivityData(dataActivity);
  //   };
  // }, [debounced]);

  useEffect(() => {
    onChange(activityData.filter((item) => item.selected));
  }, [activityData]);

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    const { checked } = e.target;
    setActivityData((prevData) =>
      prevData.map((item) => (item.activityId === id ? { ...item, selected: checked } : item))
    );
  };

  const columns = [
    {
      heading: 'Activity Name',
      props: 'activityName',
      active: true,
      sortable: true
    },
    {
      heading: 'Activity Type',
      props: 'activityType',
      active: true,
      sortable: true
    },
    {
      heading: 'Beelajar LMS Link',
      props: ({ activityUrl }: { activityUrl: string }) => (
        <MaterialLink linkMaterial={activityUrl} title={'Link'} isBeelajarLoginLink={true} />
      ),
      active: true,
      sortable: false
    },
    {
      heading: '',
      active: true,
      props: (row: any) => {
        return (
          <Checkbox
            defaultChecked={row.selected}
            checked={row.selected}
            onChange={(e) => handleCheckbox(e, row.activityId)}
          />
        );
      }
    }
  ];

  const getNothingFoundMessage = () => {
    if (isFetching) {
      return 'Loading...';
    }

    if (searchValue) {
      return `Course with '${searchValue}' not found, try another course...`;
    }

    return 'Try to search by course name';
  };

  return (
    <>
      <Stack mt={'sm'}>
        <Select
          leftSection={<IconSearch size={20} />}
          label='Select Courses'
          placeholder='Select courses'
          data={
            data?.data.map((course: { courseID: string; courseName: string }) => ({
              label: course.courseName,
              value: course.courseID
            })) || []
          }
          searchable
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onChange={(value) => setSelectedCourses(value)}
          nothingFoundMessage={getNothingFoundMessage()}
        />

        {selectedCourses && (
          <Paper withBorder p={16} radius={'lg'}>
            <DataTables
              data={activityData}
              columns={columns}
              withPagination={false}
              loading={loadingGetSection}
              emptyMessage='No activity found'
            />
          </Paper>
        )}
      </Stack>
    </>
  );
};

export default ExistingActivity;
